import { useEffect, useRef } from 'react';
import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react';

const MIN_DRAG_DISTANCE = 20;

export type SheetPosition = 'collapsed' | 'default' | 'expanded';

export const SHEET_POSITIONS: readonly SheetPosition[] = ['collapsed', 'default', 'expanded'];

/** Clamps to the next position in `delta`'s direction; a no-op at either end of the range. */
export const stepPosition = (position: SheetPosition, delta: 1 | -1): SheetPosition => {
  const index = SHEET_POSITIONS.indexOf(position);
  const nextIndex = Math.min(SHEET_POSITIONS.length - 1, Math.max(0, index + delta));
  return SHEET_POSITIONS[nextIndex];
};

type ThreePositionDragOptions = {
  position: SheetPosition;
  onPositionChange: (position: SheetPosition) => void;
  /** Pass false to ignore new gestures and cancel one in progress. */
  enabled?: boolean;
  /** Called when a gesture starts, before the pointer has moved. */
  onDragStart?: () => void;
  /** Called while the pointer moves past the drag threshold. `distance` is the signed clientY delta from the start. */
  onDragMove?: (distance: number, startedPosition: SheetPosition) => void;
  /** Called when a gesture ends or is cancelled, before `onPositionChange`. */
  onDragEnd?: () => void;
};

/**
 * Pointer-drag and click handling for a 3-position pill toggle (collapsed / default / expanded).
 * Mirrors `usePillDrag`'s gesture rules (20px threshold, primary pointer only, the click that
 * follows a drag is ignored) but steps exactly one position per gesture, toward whichever end the
 * drag's direction points to, instead of toggling a boolean.
 */
export const useThreePositionDrag = ({
  position,
  onPositionChange,
  enabled = true,
  onDragStart,
  onDragMove,
  onDragEnd,
}: ThreePositionDragOptions) => {
  const dragRef = useRef<{
    pointerId: number;
    startY: number;
    position: SheetPosition;
    moved: boolean;
    button: HTMLButtonElement;
  } | null>(null);
  const suppressClick = useRef(false);

  const releaseCapture = (drag: NonNullable<typeof dragRef.current>) => {
    if (drag.button.hasPointerCapture(drag.pointerId)) {
      drag.button.releasePointerCapture(drag.pointerId);
    }
  };

  useEffect(() => {
    if (!enabled && dragRef.current) {
      const drag = dragRef.current;
      dragRef.current = null;
      suppressClick.current = false;
      onDragEnd?.();
      onPositionChange(drag.position);
      releaseCapture(drag);
    }
  }, [enabled, onPositionChange, onDragEnd]);

  useEffect(
    () => () => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (drag) {
        releaseCapture(drag);
      }
    },
    [],
  );

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!enabled || event.button !== 0 || !event.isPrimary || dragRef.current) {
      return;
    }
    event.stopPropagation();
    suppressClick.current = false;
    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      position,
      moved: false,
      button: event.currentTarget,
    };
    onDragStart?.();
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const distance = event.clientY - drag.startY;
    if (Math.abs(distance) > MIN_DRAG_DISTANCE) {
      drag.moved = true;
    }
    if (drag.moved) {
      onDragMove?.(distance, drag.position);
    }
  };

  const onPointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const distance = event.clientY - drag.startY;
    const moved = drag.moved || Math.abs(distance) > MIN_DRAG_DISTANCE;
    suppressClick.current = moved;
    dragRef.current = null;
    onDragEnd?.();
    if (moved) {
      const target =
        Math.abs(distance) > MIN_DRAG_DISTANCE ? stepPosition(drag.position, distance < 0 ? 1 : -1) : drag.position;
      onPositionChange(target);
    }
    releaseCapture(drag);
  };

  const onPointerCancel = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    dragRef.current = null;
    onDragEnd?.();
    suppressClick.current = drag.moved;
    onPositionChange(drag.position);
    releaseCapture(drag);
  };

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Captured releases can synthesize a click; keyboard activation has detail 0.
    const suppressed = suppressClick.current && event.detail !== 0;
    suppressClick.current = false;
    if (!suppressed) {
      // A tap/keyboard activation always lands on 'default', except from 'default' itself, where
      // it goes to 'expanded'. A tap from 'collapsed' steps back up to 'default' rather than
      // jumping straight to 'expanded'.
      onPositionChange(position === 'default' ? 'expanded' : 'default');
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!enabled || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onPositionChange(stepPosition(position, event.key === 'ArrowUp' ? 1 : -1));
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture: onPointerCancel,
    onClick,
    onKeyDown,
  };
};
